import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import {
  DRY_LEASE_TERM_MONTHS,
  FACTORY_LEAD_MONTHS_MAX,
  FACTORY_LEAD_MONTHS_MIN,
  FINANCE_LEASE_TERM_MONTHS,
  LEASE_AGE_YEARS,
  WET_LEASE_TERM_MONTHS,
  buildAircraftSpecGrid,
  estimateOwnershipCasmCents,
  factoryDepositUSD,
  factoryListPriceUSD,
  factoryLockedPriceUSD,
  purchasedDailyCapitalCostUSD,
  quoteLease,
} from '../../../domain/fleet';
import type { AircraftSpecGrid, MarketEnvironment } from '../../../domain/fleet';
import { createDefaultCabinConfiguration, getTotalSeats } from '../../../domain/economics';
import { decodeSimHour, formatSimDate } from '../../../domain/demand/simClock';
import type { AircraftType, CabinConfiguration, SeatClass, SimMinutes } from '../../../domain/models';
import type {
  FactoryOrderView,
  FleetAcquisitionView,
  FleetAircraftView,
  LeaseSubTypeCommand,
  LegView,
  SecondaryListingView,
  LeaseView,
  UICommand,
} from '../../../domain/models/contracts';
import { formatInt, formatUsd } from '../analytics/formatters';
import './FleetAcquisitionPanel.css';

export interface FleetAcquisitionPanelProps {
  readonly acquisitions: FleetAcquisitionView;
  readonly aircraftTypes: readonly AircraftType[];
  readonly fleet: readonly FleetAircraftView[];
  readonly legs: readonly LegView[];
  /** Current absolute simulation hour. */
  readonly hour: number;
  /** Cash on hand, or null before the first network state. */
  readonly cashUSD: number | null;
  readonly onCommand: (command: UICommand) => void;
}

type Tab = 'market' | 'order' | 'contracts';

type ListingSort = 'PRICE' | 'AGE' | 'SEATS' | 'CONDITION';

const LISTING_SORTS: ReadonlyArray<{ readonly id: ListingSort; readonly label: string }> = [
  { id: 'PRICE', label: 'Asking price, low first' },
  { id: 'AGE', label: 'Youngest first' },
  { id: 'SEATS', label: 'Most seats first' },
  { id: 'CONDITION', label: 'Best condition first' },
];

const TABS: ReadonlyArray<{ readonly id: Tab; readonly label: string }> = [
  { id: 'market', label: 'Used market' },
  { id: 'order', label: 'Order & lease' },
  { id: 'contracts', label: 'Contracts' },
];

const LEASE_TERMS: Readonly<Record<LeaseSubTypeCommand, { readonly min: number; readonly max: number; readonly label: string }>> = {
  DRY: { min: DRY_LEASE_TERM_MONTHS.min, max: DRY_LEASE_TERM_MONTHS.max, label: 'Dry lease' },
  WET: { min: WET_LEASE_TERM_MONTHS.min, max: WET_LEASE_TERM_MONTHS.max, label: 'Wet lease (ACMI)' },
  FINANCE: { min: FINANCE_LEASE_TERM_MONTHS.min, max: FINANCE_LEASE_TERM_MONTHS.max, label: 'Finance lease' },
};

const SALE_LEASEBACK_TERM = { min: DRY_LEASE_TERM_MONTHS.min, max: DRY_LEASE_TERM_MONTHS.max };
const MINUTES_PER_MONTH = 30 * 24 * 60;

function dateOf(minutes: SimMinutes): string {
  return formatSimDate(decodeSimHour(Math.floor(minutes / 60)));
}

/** Whole months from `hour` until `minutes`, never negative. */
function monthsUntil(minutes: SimMinutes, hour: number): number {
  return Math.max(0, Math.round((minutes - hour * 60) / MINUTES_PER_MONTH));
}

function clampInt(value: number, min: number, max: number): number {
  return Number.isFinite(value) ? Math.min(max, Math.max(min, Math.round(value))) : min;
}

/** Quoted lease terms use a whole-year age inside the lessor's range. */
function leaseAgeOf(ageYears: number): number {
  return clampInt(ageYears, LEASE_AGE_YEARS.min, LEASE_AGE_YEARS.max);
}

const SEAT_CLASS_CODES: Readonly<Record<SeatClass, { readonly code: string; readonly name: string }>> = {
  ECONOMY: { code: 'Y', name: 'Economy' },
  PREMIUM_ECONOMY: { code: 'W', name: 'Premium economy' },
  BUSINESS: { code: 'J', name: 'Business' },
  FIRST: { code: 'F', name: 'First' },
};
const SEAT_CLASS_ORDER: readonly SeatClass[] = ['ECONOMY', 'PREMIUM_ECONOMY', 'BUSINESS', 'FIRST'];

/**
 * Fleet acquisition: the monthly used-aircraft market, factory orders and new
 * leases, plus the contracts already in force. Every action is a worker
 * command; the engine validates it and reports refusals as errors.
 */
export function FleetAcquisitionPanel({
  acquisitions,
  aircraftTypes,
  fleet,
  legs,
  hour,
  cashUSD,
  onCommand,
}: FleetAcquisitionPanelProps): ReactElement {
  const [tab, setTab] = useState<Tab>('market');
  const typesById = useMemo<ReadonlyMap<string, AircraftType>>(
    () => new Map(aircraftTypes.map((t) => [t.id, t] as const)),
    [aircraftTypes],
  );
  const sortedTypes = useMemo(
    () => [...aircraftTypes].sort((a, b) => a.name.localeCompare(b.name)),
    [aircraftTypes],
  );
  const legCountByReg = useMemo(() => {
    const counts = new Map<string, number>();
    for (const leg of legs) counts.set(leg.aircraftReg, (counts.get(leg.aircraftReg) ?? 0) + 1);
    return counts;
  }, [legs]);
  const market = acquisitions.market;
  const [typeFilter, setTypeFilter] = useState<string>('ALL');
  const [sortKey, setSortKey] = useState<ListingSort>('PRICE');
  const seatsByTypeId = useMemo(
    () => new Map(aircraftTypes.map((t) => [t.id, getTotalSeats(createDefaultCabinConfiguration(t))] as const)),
    [aircraftTypes],
  );
  const listingTypeOptions = useMemo(() => {
    const ids = [...new Set(acquisitions.listings.map((l) => l.typeId))];
    return ids
      .map((id) => ({ id, name: typesById.get(id)?.name ?? id }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [acquisitions.listings, typesById]);
  const visibleListings = useMemo(() => {
    const filtered = acquisitions.listings.filter((l) => typeFilter === 'ALL' || l.typeId === typeFilter);
    const seats = (typeId: string): number => seatsByTypeId.get(typeId) ?? 0;
    const compare: Record<ListingSort, (a: SecondaryListingView, b: SecondaryListingView) => number> = {
      PRICE: (a, b) => a.askingPriceUSD - b.askingPriceUSD,
      AGE: (a, b) => a.ageYears - b.ageYears,
      SEATS: (a, b) => seats(b.typeId) - seats(a.typeId),
      CONDITION: (a, b) => b.conditionPct - a.conditionPct,
    };
    return [...filtered].sort(compare[sortKey]);
  }, [acquisitions.listings, typeFilter, sortKey, seatsByTypeId]);

  const openOrders = acquisitions.factoryOrders.filter((o) => o.status === 'OPEN');
  const activeLeases = acquisitions.leases.filter((l) => l.status === 'ACTIVE');
  const ownedForSale = fleet.filter((a) => a.purchaseType === 'PURCHASE');
  const counts: Record<Tab, number | null> = {
    market: acquisitions.listings.length,
    order: null,
    contracts: openOrders.length + activeLeases.length,
  };

  return (
    <section className="fap" aria-label="Fleet acquisition">
      <header className="fap-header">
        <div>
          <h2 className="fap-title">Fleet acquisition</h2>
          <p className="fap-subtitle">
            Used aircraft listings refresh monthly. Lessor rates for this month: {market.annualInterestRatePct.toFixed(1)}% rate, demand index{' '}
            {market.demandIndex.toFixed(2)}.
          </p>
        </div>
      </header>

      <div className="fap-tabs" role="tablist" aria-label="Fleet acquisition sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`fap-tab-${t.id}`}
            aria-selected={tab === t.id}
            aria-controls={`fap-panel-${t.id}`}
            className={`fap-tab${tab === t.id ? ' is-active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
            {counts[t.id] !== null && <span className="fap-count">{counts[t.id]}</span>}
          </button>
        ))}
      </div>

      {tab === 'market' && (
        <div role="tabpanel" id="fap-panel-market" aria-labelledby="fap-tab-market" className="fap-body">
          <h3 className="fap-heading">Secondary listings</h3>
          {acquisitions.listings.length === 0 ? (
            <p className="fap-empty">No used aircraft listed this month.</p>
          ) : (
            <>
              <div className="fap-toolbar">
                <label className="fap-field">
                  <span>Aircraft type</span>
                  <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                    <option value="ALL">All types ({acquisitions.listings.length})</option>
                    {listingTypeOptions.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="fap-field">
                  <span>Sort by</span>
                  <select value={sortKey} onChange={(e) => setSortKey(e.target.value as ListingSort)}>
                    {LISTING_SORTS.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                <p className="fap-toolbar-count" aria-live="polite">
                  Showing {visibleListings.length} of {acquisitions.listings.length}
                </p>
              </div>
              {visibleListings.length === 0 ? (
                <p className="fap-empty">No listings for this type this month.</p>
              ) : (
            <div className="fap-card-grid">
              {visibleListings.map((listing) => {
                const type = typesById.get(listing.typeId);
                if (type === undefined) return null;
                return (
                  <ListingCard
                    key={listing.listingId}
                    listing={listing}
                    type={type}
                    market={market}
                    cashUSD={cashUSD}
                    onBuy={() =>
                      onCommand({
                        type: 'PURCHASE_AIRCRAFT',
                        typeId: type.id,
                        channel: { kind: 'SECONDARY_LISTING', listingId: listing.listingId },
                        cabin: createDefaultCabinConfiguration(type),
                      })
                    }
                  />
                );
              })}
            </div>
              )}
            </>
          )}

          <h3 className="fap-heading">Sell and lease back</h3>
          <p className="fap-note">
            Sell an owned airframe to a lessor for 90% of its appraised value and keep flying it on a dry lease.
          </p>
          {ownedForSale.length === 0 ? (
            <p className="fap-empty">No owned aircraft to sell.</p>
          ) : (
            <SaleLeasebackRows
              aircraft={ownedForSale}
              typeName={(id) => typesById.get(id)?.name ?? id}
              onSell={(registration, termMonths) =>
                onCommand({ type: 'SELL_LEASEBACK_AIRCRAFT', registration, termMonths })
              }
            />
          )}
        </div>
      )}

      {tab === 'order' && (
        <div role="tabpanel" id="fap-panel-order" aria-labelledby="fap-tab-order" className="fap-body fap-grid-2">
          <FactoryOrderForm
            aircraftTypes={sortedTypes}
            market={market}
            cashUSD={cashUSD}
            onOrder={(type, deliveryLeadMonths) =>
              onCommand({
                type: 'PURCHASE_AIRCRAFT',
                typeId: type.id,
                channel: { kind: 'FACTORY_ORDER', deliveryLeadMonths },
                cabin: createDefaultCabinConfiguration(type),
              })
            }
          />
          <LeaseForm
            aircraftTypes={sortedTypes}
            market={market}
            cashUSD={cashUSD}
            onLease={(type, leaseSubType, termMonths, ageYears) =>
              onCommand({
                type: 'LEASE_AIRCRAFT',
                typeId: type.id,
                leaseSubType,
                termMonths,
                ageYears,
                cabin: createDefaultCabinConfiguration(type),
              })
            }
          />
        </div>
      )}

      {tab === 'contracts' && (
        <div role="tabpanel" id="fap-panel-contracts" aria-labelledby="fap-tab-contracts" className="fap-body">
          <h3 className="fap-heading">Factory orders in progress</h3>
          {openOrders.length === 0 ? (
            <p className="fap-empty">No open factory orders.</p>
          ) : (
            <div className="fap-table-wrap">
              <table className="fap-table">
                <thead>
                  <tr>
                    <th scope="col">Order</th>
                    <th scope="col">Type</th>
                    <th scope="col">Reg</th>
                    <th scope="col">Delivers</th>
                    <th scope="col" className="num">Locked price</th>
                    <th scope="col" className="num">Deposit paid</th>
                    <th scope="col" className="num">Due at delivery</th>
                  </tr>
                </thead>
                <tbody>
                  {openOrders.map((order) => (
                    <OrderRow key={order.id} order={order} typeName={typesById.get(order.typeId)?.name ?? order.typeId} hour={hour} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h3 className="fap-heading">Leases in force</h3>
          {activeLeases.length === 0 ? (
            <p className="fap-empty">No active leases.</p>
          ) : (
            <div className="fap-table-wrap">
              <table className="fap-table">
                <thead>
                  <tr>
                    <th scope="col">Reg</th>
                    <th scope="col">Type</th>
                    <th scope="col">Plan</th>
                    <th scope="col" className="num">Paid</th>
                    <th scope="col" className="num">Monthly</th>
                    <th scope="col" className="num">Balance</th>
                    <th scope="col">Ends</th>
                    <th scope="col"><span className="visually-hidden">Action</span></th>
                  </tr>
                </thead>
                <tbody>
                  {activeLeases.map((lease) => (
                    <LeaseRow
                      key={lease.registration}
                      lease={lease}
                      typeName={typesById.get(lease.typeId)?.name ?? lease.typeId}
                      hour={hour}
                      legCount={legCountByReg.get(lease.registration) ?? 0}
                      onTerminate={() => onCommand({ type: 'TERMINATE_LEASE', registration: lease.registration })}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------------ */

interface ListingCardProps {
  readonly listing: SecondaryListingView;
  readonly type: AircraftType;
  readonly market: MarketEnvironment;
  readonly cashUSD: number | null;
  readonly onBuy: () => void;
}

function ListingCard({ listing, type, market, cashUSD, onBuy }: ListingCardProps): ReactElement {
  const cabin = useMemo(() => createDefaultCabinConfiguration(type), [type]);
  const grid = useMemo(
    () =>
      buildAircraftSpecGrid({
        type,
        cabin,
        airframeHours: listing.airframeHours,
        fuelBurnMultiplier: listing.fuelBurnMultiplier,
      }),
    [type, cabin, listing.airframeHours, listing.fuelBurnMultiplier],
  );
  const dryRateUSD = quoteLease('DRY', LEASE_TERMS.DRY.min, leaseAgeOf(listing.ageYears), type, market).monthlyPaymentUSD;
  const casmCents = estimateOwnershipCasmCents({
    seats: grid.totalSeats,
    cruiseSpeedKmh: type.cruiseSpeedKmh,
    dailyCapitalCostUSD: purchasedDailyCapitalCostUSD(listing.askingPriceUSD, market.annualInterestRatePct),
  });
  const cannotAfford = cashUSD !== null && listing.askingPriceUSD > cashUSD;

  return (
    <article className="fap-spec-card" aria-label={`${type.name} ${listing.registration}`}>
      <header className="fap-spec-head">
        <div>
          <h4 className="fap-spec-title">{type.name}</h4>
          <p className="fap-muted">
            {listing.registration} · built {listing.manufactureYear} · {listing.ageYears} yr old
          </p>
        </div>
        <span className="fap-condition" title="Airframe condition">{listing.conditionPct}% condition</span>
      </header>
      <SpecGrid grid={grid} />
      <FinancialGrid
        downPaymentUSD={listing.askingPriceUSD}
        downPaymentLabel="Cash to buy"
        monthlyUSD={dryRateUSD}
        casmCents={casmCents}
      />
      <div className="fap-spec-foot">
        <span className="fap-muted">Lapses on day {listing.expiresOnDay}</span>
        <button
          type="button"
          className="btn btn-small btn-primary"
          onClick={onBuy}
          disabled={cannotAfford}
          title={cannotAfford ? 'Not enough cash for the asking price' : `Buy ${listing.registration}`}
        >
          Buy for {formatUsd(listing.askingPriceUSD)}
        </button>
      </div>
    </article>
  );
}

/** Configured seats by class, cargo payload, range, speed, burn and check status. */
function SpecGrid({ grid }: { readonly grid: AircraftSpecGrid }): ReactElement {
  const seatsLabel = SEAT_CLASS_ORDER.filter((c) => grid.seatsByClass[c] > 0)
    .map((c) => `${SEAT_CLASS_CODES[c].code} ${grid.seatsByClass[c]}`)
    .join(' · ');
  return (
    <dl className="fap-spec">
      <div className="fap-spec-wide">
        <dt>Seats by class</dt>
        <dd title={SEAT_CLASS_ORDER.map((c) => `${SEAT_CLASS_CODES[c].name}: ${grid.seatsByClass[c]}`).join(', ')}>
          {seatsLabel || 'No seats'} <span className="fap-muted">({formatInt(grid.totalSeats)} total)</span>
        </dd>
      </div>
      <div>
        <dt>Cargo payload</dt>
        <dd>{formatInt(grid.cargoPayloadKg)} kg</dd>
      </div>
      <div>
        <dt>Range</dt>
        <dd>{formatInt(grid.rangeKm)} km</dd>
      </div>
      <div>
        <dt>Cruise</dt>
        <dd>Mach {grid.cruiseMach.toFixed(2)}</dd>
      </div>
      <div>
        <dt>Fuel burn</dt>
        <dd>{formatInt(grid.fuelBurnKgPerHour)} kg/h</dd>
      </div>
      <div>
        <dt>Airframe hours</dt>
        <dd>{formatInt(grid.airframeHours)} h</dd>
      </div>
      <div>
        <dt>To next A-check</dt>
        <dd>{formatInt(grid.hoursToACheck)} h</dd>
      </div>
      <div>
        <dt>To next C-check</dt>
        <dd>{formatInt(grid.hoursToCCheck)} h</dd>
      </div>
    </dl>
  );
}

interface FinancialGridProps {
  readonly downPaymentUSD: number;
  readonly downPaymentLabel: string;
  readonly monthlyUSD: number;
  readonly casmCents: number;
}

/** Cash up front, the lease rate for the same airframe, and the ownership CASM impact. */
function FinancialGrid({ downPaymentUSD, downPaymentLabel, monthlyUSD, casmCents }: FinancialGridProps): ReactElement {
  return (
    <dl className="fap-finance">
      <div>
        <dt>{downPaymentLabel}</dt>
        <dd>{formatUsd(downPaymentUSD)}</dd>
      </div>
      <div>
        <dt>Dry lease / month</dt>
        <dd>{formatUsd(monthlyUSD)}</dd>
      </div>
      <div>
        <dt>Est. CASM impact</dt>
        <dd title="Ownership capital charge per available seat mile, at 10 block hours a day. Fuel, crew and maintenance are excluded.">
          +{casmCents.toFixed(2)}¢ /ASM
        </dd>
      </div>
    </dl>
  );
}

interface SaleLeasebackRowsProps {
  readonly aircraft: readonly FleetAircraftView[];
  readonly typeName: (typeId: string) => string;
  readonly onSell: (registration: string, termMonths: number) => void;
}

function SaleLeasebackRows({ aircraft, typeName, onSell }: SaleLeasebackRowsProps): ReactElement {
  const [termMonths, setTermMonths] = useState<number>(72);
  const term = clampInt(termMonths, SALE_LEASEBACK_TERM.min, SALE_LEASEBACK_TERM.max);
  return (
    <div className="fap-inline">
      <label className="fap-field">
        <span>Leaseback term (months)</span>
        <input
          type="number"
          min={SALE_LEASEBACK_TERM.min}
          max={SALE_LEASEBACK_TERM.max}
          value={termMonths}
          onChange={(e) => setTermMonths(Number(e.target.value))}
        />
      </label>
      <ul className="fap-chips" aria-label="Owned aircraft">
        {aircraft.map((a) => (
          <li key={a.registration}>
            <button
              type="button"
              className="btn btn-small"
              onClick={() => onSell(a.registration, term)}
              title={`Sell ${a.registration} and lease it back for ${term} months`}
            >
              Sell {a.registration} ({typeName(a.typeId)})
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface FactoryOrderFormProps {
  readonly aircraftTypes: readonly AircraftType[];
  readonly market: MarketEnvironment;
  readonly cashUSD: number | null;
  readonly onOrder: (type: AircraftType, deliveryLeadMonths: number) => void;
}

function FactoryOrderForm({ aircraftTypes, market, cashUSD, onOrder }: FactoryOrderFormProps): ReactElement {
  const [typeId, setTypeId] = useState<string>(aircraftTypes[0]?.id ?? '');
  const [leadMonths, setLeadMonths] = useState<number>(12);
  const type = aircraftTypes.find((t) => t.id === typeId);
  const leadValid = Number.isInteger(leadMonths) && leadMonths >= FACTORY_LEAD_MONTHS_MIN && leadMonths <= FACTORY_LEAD_MONTHS_MAX;
  const specCabin = useMemo<CabinConfiguration | null>(() => (type === undefined ? null : createDefaultCabinConfiguration(type)), [type]);
  const grid = useMemo<AircraftSpecGrid | null>(
    () =>
      type === undefined || specCabin === null
        ? null
        : buildAircraftSpecGrid({ type, cabin: specCabin, airframeHours: 0, fuelBurnMultiplier: 1 }),
    [type, specCabin],
  );

  const preview = useMemo(() => {
    if (type === undefined || !leadValid) return null;
    const lockedPriceUSD = factoryLockedPriceUSD(type, leadMonths, market);
    const depositUSD = factoryDepositUSD(lockedPriceUSD);
    return {
      listPriceUSD: factoryListPriceUSD(type, market),
      lockedPriceUSD,
      depositUSD,
      balanceUSD: lockedPriceUSD - depositUSD,
    };
  }, [type, leadMonths, leadValid, market]);

  const cannotAfford = preview !== null && cashUSD !== null && preview.depositUSD > cashUSD;
  const canOrder = preview !== null && !cannotAfford && type !== undefined;

  return (
    <form
      className="fap-card"
      aria-labelledby="fap-order-heading"
      onSubmit={(e) => {
        e.preventDefault();
        if (canOrder && type !== undefined) onOrder(type, leadMonths);
      }}
    >
      <h3 id="fap-order-heading" className="fap-heading">New factory order</h3>
      <p className="fap-note">Build to order from the manufacturer. Pay a deposit now and the balance on delivery.</p>
      <label className="fap-field">
        <span>Aircraft type</span>
        <select value={typeId} onChange={(e) => setTypeId(e.target.value)}>
          {aircraftTypes.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </label>
      {type !== undefined && grid !== null && (
        <>
          <SpecGrid grid={grid} />
          {preview !== null && (
            <FinancialGrid
              downPaymentUSD={preview.depositUSD}
              downPaymentLabel="Deposit now"
              monthlyUSD={quoteLease('DRY', LEASE_TERMS.DRY.min, 0, type, market).monthlyPaymentUSD}
              casmCents={estimateOwnershipCasmCents({
                seats: grid.totalSeats,
                cruiseSpeedKmh: type.cruiseSpeedKmh,
                dailyCapitalCostUSD: purchasedDailyCapitalCostUSD(preview.lockedPriceUSD, market.annualInterestRatePct),
              })}
            />
          )}
        </>
      )}
      <label className="fap-field">
        <span>Delivery in (months, {FACTORY_LEAD_MONTHS_MIN}–{FACTORY_LEAD_MONTHS_MAX})</span>
        <input
          type="number"
          min={FACTORY_LEAD_MONTHS_MIN}
          max={FACTORY_LEAD_MONTHS_MAX}
          step={1}
          value={leadMonths}
          onChange={(e) => setLeadMonths(Number(e.target.value))}
          aria-invalid={!leadValid}
        />
      </label>
      {!leadValid && <p className="fap-error">Lead time must be a whole number of months between 6 and 24.</p>}
      {preview !== null && (
        <dl className="fap-figures">
          <div><dt>List price</dt><dd>{formatUsd(preview.listPriceUSD)}</dd></div>
          <div><dt>Locked price</dt><dd>{formatUsd(preview.lockedPriceUSD)}</dd></div>
          <div><dt>Deposit now</dt><dd>{formatUsd(preview.depositUSD)}</dd></div>
          <div><dt>Due at delivery</dt><dd>{formatUsd(preview.balanceUSD)}</dd></div>
        </dl>
      )}
      {cannotAfford && <p className="fap-error">The deposit is more than the cash on hand.</p>}
      <button type="submit" className="btn btn-primary" disabled={!canOrder}>
        Place factory order
      </button>
    </form>
  );
}

interface LeaseFormProps {
  readonly aircraftTypes: readonly AircraftType[];
  readonly market: MarketEnvironment;
  readonly cashUSD: number | null;
  readonly onLease: (type: AircraftType, leaseSubType: LeaseSubTypeCommand, termMonths: number, ageYears: number) => void;
}

function LeaseForm({ aircraftTypes, market, cashUSD, onLease }: LeaseFormProps): ReactElement {
  const [typeId, setTypeId] = useState<string>(aircraftTypes[0]?.id ?? '');
  const [subType, setSubType] = useState<LeaseSubTypeCommand>('DRY');
  const [termMonths, setTermMonths] = useState<number>(LEASE_TERMS.DRY.min);
  const [ageYears, setAgeYears] = useState<number>(0);
  const type = aircraftTypes.find((t) => t.id === typeId);
  const range = LEASE_TERMS[subType];
  const termValid = Number.isInteger(termMonths) && termMonths >= range.min && termMonths <= range.max;

  const selectSubType = (next: LeaseSubTypeCommand): void => {
    setSubType(next);
    setTermMonths(LEASE_TERMS[next].min);
  };

  const quote = useMemo(() => {
    if (type === undefined || !termValid) return null;
    return quoteLease(subType, termMonths, ageYears, type, market);
  }, [type, subType, termMonths, ageYears, termValid, market]);

  const cannotAfford = quote !== null && cashUSD !== null && quote.upfrontUSD > cashUSD;
  const canLease = quote !== null && !cannotAfford && type !== undefined;

  return (
    <form
      className="fap-card"
      aria-labelledby="fap-lease-heading"
      onSubmit={(e) => {
        e.preventDefault();
        if (canLease && type !== undefined) onLease(type, subType, termMonths, ageYears);
      }}
    >
      <h3 id="fap-lease-heading" className="fap-heading">New lease</h3>
      <fieldset className="fap-radio">
        <legend>Lease type</legend>
        {(Object.keys(LEASE_TERMS) as LeaseSubTypeCommand[]).map((key) => (
          <label key={key}>
            <input type="radio" name="fap-lease-subtype" value={key} checked={subType === key} onChange={() => selectSubType(key)} />
            {LEASE_TERMS[key].label}
          </label>
        ))}
      </fieldset>
      <p className="fap-note">
        {subType === 'DRY' && 'You crew, fuel and maintain the aircraft. Two months’ deposit, refunded on a clean return.'}
        {subType === 'WET' && 'The lessor supplies crew, maintenance and insurance; you pay fuel and charges. No deposit.'}
        {subType === 'FINANCE' && 'Amortising loan on the airframe. Ownership passes to you when the balloon is paid.'}
      </p>
      <label className="fap-field">
        <span>Aircraft type</span>
        <select value={typeId} onChange={(e) => setTypeId(e.target.value)}>
          {aircraftTypes.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </label>
      <div className="fap-row">
        <label className="fap-field">
          <span>Term (months, {range.min}–{range.max})</span>
          <input
            type="number"
            min={range.min}
            max={range.max}
            step={1}
            value={termMonths}
            onChange={(e) => setTermMonths(Number(e.target.value))}
            aria-invalid={!termValid}
          />
        </label>
        <label className="fap-field">
          <span>Airframe age (years)</span>
          <select value={ageYears} onChange={(e) => setAgeYears(Number(e.target.value))}>
            {Array.from({ length: LEASE_AGE_YEARS.max - LEASE_AGE_YEARS.min + 1 }, (_, i) => i + LEASE_AGE_YEARS.min).map((age) => (
              <option key={age} value={age}>
                {age === 0 ? 'New' : `${age}`}
              </option>
            ))}
          </select>
        </label>
      </div>
      {!termValid && <p className="fap-error">Term must be a whole number of months from {range.min} to {range.max}.</p>}
      {quote !== null && (
        <dl className="fap-figures">
          <div><dt>Monthly</dt><dd>{formatUsd(quote.monthlyPaymentUSD)}</dd></div>
          <div><dt>{subType === 'FINANCE' ? 'Down payment' : 'Deposit'}</dt><dd>{formatUsd(quote.depositUSD)}</dd></div>
          <div><dt>Due at signing</dt><dd>{formatUsd(quote.upfrontUSD)}</dd></div>
          {subType === 'FINANCE' && <div><dt>Balloon</dt><dd>{formatUsd(quote.balloonUSD)}</dd></div>}
        </dl>
      )}
      {cannotAfford && <p className="fap-error">Signing costs more than the cash on hand.</p>}
      <button type="submit" className="btn btn-primary" disabled={!canLease}>
        Sign {range.label.toLowerCase()}
      </button>
    </form>
  );
}

interface OrderRowProps {
  readonly order: FactoryOrderView;
  readonly typeName: string;
  readonly hour: number;
}

function OrderRow({ order, typeName, hour }: OrderRowProps): ReactElement {
  const monthsLeft = monthsUntil(order.deliveryAt, hour);
  return (
    <tr>
      <td>{order.id}</td>
      <td>{typeName}</td>
      <td>{order.registration}</td>
      <td>
        {dateOf(order.deliveryAt)} <span className="fap-muted">({monthsLeft} mo)</span>
      </td>
      <td className="num">{formatUsd(order.lockedPriceUSD)}</td>
      <td className="num">{formatUsd(order.depositUSD)}</td>
      <td className="num">{formatUsd(order.lockedPriceUSD - order.depositUSD)}</td>
    </tr>
  );
}

interface LeaseRowProps {
  readonly lease: LeaseView;
  readonly typeName: string;
  readonly hour: number;
  readonly legCount: number;
  readonly onTerminate: () => void;
}

function LeaseRow({ lease, typeName, hour, legCount, onTerminate }: LeaseRowProps): ReactElement {
  const blocked = legCount > 0;
  const plan = LEASE_TERMS[lease.subType].label + (lease.origin === 'SALE_LEASEBACK' ? ' (leaseback)' : '');
  return (
    <tr>
      <td>{lease.registration}</td>
      <td>{typeName}</td>
      <td>{plan}</td>
      <td className="num">
        {lease.paymentsMade}/{lease.termMonths}
      </td>
      <td className="num">{formatUsd(lease.monthlyPaymentUSD)}</td>
      <td className="num">{lease.outstandingPrincipalUSD !== null ? formatUsd(lease.outstandingPrincipalUSD) : formatUsd(lease.depositUSD)}</td>
      <td>
        {dateOf(lease.endsAt)} <span className="fap-muted">({monthsUntil(lease.endsAt, hour)} mo)</span>
      </td>
      <td>
        <button
          type="button"
          className="btn btn-small"
          onClick={onTerminate}
          disabled={blocked}
          title={blocked ? `Remove the ${legCount} flight leg(s) on ${lease.registration} first` : `End the lease on ${lease.registration}`}
        >
          Terminate
        </button>
      </td>
    </tr>
  );
}
